import React, { useEffect, useState, useCallback } from 'react';
import VisibilityEditor from '../components/VisibilityEditor';

const EMPTY_ITEM = {
  title: '', body: '', type: 'link', url: '', linkText: '', thumbnailUrl: '', thumbnailWidth: '',
  sectionId: '', sortOrder: 0, visibleTiers: [], visibleUserIds: [],
};
const EMPTY_SECTION = { name: '' };

function itemVisibleUserIds(item) {
  if (Array.isArray(item.visibleUserIds)) return item.visibleUserIds;
  return item.visibleUserId ? [item.visibleUserId] : [];
}

export default function AdminDashboardPage() {
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null); // null | 'new' | item
  const [form, setForm] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);

  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState(EMPTY_SECTION);
  const [savingSection, setSavingSection] = useState(false);

  const [draggedSectionId, setDraggedSectionId] = useState(null);
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverSectionId, setDragOverSectionId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, sectionsRes, usersRes] = await Promise.all([
        fetch('/api/manage-dashboard', { credentials: 'include' }),
        fetch('/api/manage-sections?context=dashboard', { credentials: 'include' }),
        fetch('/api/manage-users', { credentials: 'include' }),
      ]);
      if (!itemsRes.ok) throw new Error('Failed to load dashboard items');
      if (!sectionsRes.ok) throw new Error('Failed to load sections');
      setItems(await itemsRes.json());
      setSections(await sectionsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Item CRUD ───
  const handleNew = () => { setEditing('new'); setForm(EMPTY_ITEM); setError(''); };
  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      ...EMPTY_ITEM,
      ...item,
      thumbnailWidth: item.thumbnailWidth ?? '',
      visibleTiers: item.visibleTiers || [],
      visibleUserIds: itemVisibleUserIds(item),
    });
    setError('');
  };
  const handleCancel = () => { setEditing(null); setForm(EMPTY_ITEM); setError(''); };
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleThumbnailFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 1_500_000) {
      setError('Image is too large (max ~1.5MB). Use a URL instead for large images.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, thumbnailUrl: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const isNew = editing === 'new';
      const url = isNew ? '/api/manage-dashboard' : `/api/manage-dashboard/${editing.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = `Save failed (HTTP ${res.status})`;
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      handleCancel();
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      const res = await fetch(`/api/manage-dashboard/${item.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  // ─── Section CRUD ───
  const handleNewSection = () => { setEditingSection('new'); setSectionForm(EMPTY_SECTION); setError(''); };
  const handleEditSection = (s) => { setEditingSection(s); setSectionForm({ name: s.name }); setError(''); };
  const handleCancelSection = () => { setEditingSection(null); setSectionForm(EMPTY_SECTION); setError(''); };

  const handleSaveSection = async (e) => {
    e.preventDefault();
    setSavingSection(true);
    setError('');
    try {
      const isNew = editingSection === 'new';
      const url = isNew ? '/api/manage-sections' : `/api/manage-sections/${editingSection.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...sectionForm, context: 'dashboard' }),
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Save failed';
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      handleCancelSection();
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSection(false);
    }
  };

  const handleDeleteSection = async (s) => {
    if (!window.confirm(`Delete section "${s.name}"? Items in it will become unassigned.`)) return;
    try {
      const res = await fetch(`/api/manage-sections/${s.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  // ─── Drag-and-drop for sections ───
  const handleSectionDragStart = (e, sectionId) => {
    setDraggedSectionId(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e, sectionId) => {
    e.preventDefault();
    if (draggedSectionId && sectionId !== draggedSectionId) {
      setDragOverSectionId(sectionId);
    }
  };

  const handleSectionDrop = async (e, targetSectionId) => {
    e.preventDefault();
    setDragOverSectionId(null);
    if (!draggedSectionId || draggedSectionId === targetSectionId) {
      setDraggedSectionId(null);
      return;
    }

    const newSections = [...sections];
    const dragIdx = newSections.findIndex((s) => s.id === draggedSectionId);
    const dropIdx = newSections.findIndex((s) => s.id === targetSectionId);
    const [moved] = newSections.splice(dragIdx, 1);
    newSections.splice(dropIdx, 0, moved);

    const reordered = newSections.map((s, i) => ({ ...s, order: i }));
    setSections(reordered);
    setDraggedSectionId(null);

    try {
      await fetch('/api/manage-sections-order', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections: reordered.map((s) => ({ id: s.id, order: s.order })) }),
        credentials: 'include',
      });
    } catch (err) {
      setError(err.message);
      await fetchData();
    }
  };

  const handleSectionDragEnd = () => {
    setDraggedSectionId(null);
    setDragOverSectionId(null);
  };

  // ─── Drag-and-drop for items within a section ───
  const handleItemDragStart = (e, itemId) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleItemDragOver = (e, itemId) => {
    e.preventDefault();
    if (draggedItemId && itemId !== draggedItemId) {
      setDragOverItemId(itemId);
    }
  };

  const handleItemDrop = async (e, targetItemId, sectionId) => {
    e.preventDefault();
    setDragOverItemId(null);
    if (!draggedItemId || draggedItemId === targetItemId) {
      setDraggedItemId(null);
      return;
    }

    const sectionItems = items
      .filter((it) => (it.sectionId || '') === sectionId)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const dragIdx = sectionItems.findIndex((it) => it.id === draggedItemId);
    const dropIdx = sectionItems.findIndex((it) => it.id === targetItemId);
    if (dragIdx === -1 || dropIdx === -1) {
      setDraggedItemId(null);
      return;
    }
    const [moved] = sectionItems.splice(dragIdx, 1);
    sectionItems.splice(dropIdx, 0, moved);

    const updates = sectionItems.map((it, i) => ({ ...it, sortOrder: i }));
    setItems(items.map((it) => updates.find((u) => u.id === it.id) || it));
    setDraggedItemId(null);

    try {
      const res = await fetch('/api/manage-dashboard-order', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: updates.map((u) => ({ id: u.id, sortOrder: u.sortOrder })) }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Reorder failed');
    } catch (err) {
      setError(err.message);
      await fetchData();
    }
  };

  const handleItemDragEnd = () => {
    setDraggedItemId(null);
    setDragOverItemId(null);
  };

  if (loading) return <div className="page"><div className="loading">Loading...</div></div>;

  const visibilityLabel = (it) => {
    const userIds = itemVisibleUserIds(it);
    const tiers = it.visibleTiers || [];
    if (tiers.length === 0 && userIds.length === 0) return 'Everyone';
    const parts = [];
    if (tiers.length > 0) parts.push(tiers.join(', '));
    if (userIds.length > 0) parts.push(`${userIds.length} user${userIds.length === 1 ? '' : 's'}`);
    return parts.join(' + ');
  };

  const itemsForSection = (sectionId) =>
    items
      .filter((it) => (it.sectionId || '') === sectionId)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  const renderItemRow = (it, sectionId) => (
    <div
      key={it.id}
      className={`section-paddle-item ${draggedItemId === it.id ? 'dragging' : ''} ${dragOverItemId === it.id ? 'drag-over' : ''}`}
      draggable
      onDragStart={(e) => handleItemDragStart(e, it.id)}
      onDragOver={(e) => handleItemDragOver(e, it.id)}
      onDrop={(e) => handleItemDrop(e, it.id, sectionId)}
      onDragEnd={handleItemDragEnd}
    >
      <span className="drag-handle" title="Drag to reorder">⠿</span>
      <span className="item-name">{it.title}</span>
      <span className="item-meta">{it.type}</span>
      <span className="item-meta">{visibilityLabel(it)}</span>
      <span className="td-actions">
        <button className="btn btn-sm" onClick={() => handleEdit(it)}>Edit</button>
        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(it)}>Delete</button>
      </span>
    </div>
  );

  const unassignedItems = itemsForSection('');

  return (
    <div className="page admin-page">
      <div className="admin-header">
        <h1>Manage Dashboard</h1>
        <div className="admin-header-actions">
          {!editing && <button className="btn btn-primary" onClick={handleNew}>+ Add Content</button>}
          {!editingSection && <button className="btn btn-secondary" onClick={handleNewSection}>+ Add Section</button>}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {editingSection && (
        <form className="admin-form" onSubmit={handleSaveSection}>
          <h2>{editingSection === 'new' ? 'New Section' : 'Edit Section'}</h2>
          <div className="form-group">
            <label htmlFor="dsectionName">Section Name *</label>
            <input
              id="dsectionName"
              value={sectionForm.name}
              onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
              required
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={savingSection}>
              {savingSection ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancelSection}>Cancel</button>
          </div>
        </form>
      )}

      {editing && (
        <form className="admin-form" onSubmit={handleSave}>
          <h2>{editing === 'new' ? 'New Content' : 'Edit Content'}</h2>
          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select id="type" name="type" value={form.type} onChange={handleChange}>
              <option value="link">Link</option>
              <option value="video">YouTube Video</option>
              <option value="article">Article</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input id="title" name="title" value={form.title} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="body">{form.type === 'article' ? 'Article Body' : 'Description'}</label>
            <textarea id="body" name="body" value={form.body} onChange={handleChange} rows={form.type === 'article' ? 6 : 3} />
          </div>
          <div className="form-group">
            <label htmlFor="url">{form.type === 'video' ? 'YouTube URL' : 'Link URL'}</label>
            <input id="url" name="url" value={form.url} onChange={handleChange} placeholder={form.type === 'video' ? 'https://youtube.com/watch?v=...' : 'https://...'} />
          </div>
          {form.type !== 'video' && (
            <>
              <div className="form-group">
                <label htmlFor="linkText">Link Text</label>
                <input id="linkText" name="linkText" value={form.linkText} onChange={handleChange} placeholder={form.type === 'article' ? 'Read more →' : 'Open link →'} />
                <small className="form-hint">Leave blank to use the default.</small>
              </div>
              <div className="form-group">
                <label htmlFor="thumbnailUrl">Thumbnail Image URL</label>
                <input id="thumbnailUrl" name="thumbnailUrl" value={form.thumbnailUrl} onChange={handleChange} placeholder="https://... or upload below" />
              </div>
              <div className="form-group">
                <label htmlFor="thumbnailFile">Or Upload Thumbnail</label>
                <input id="thumbnailFile" type="file" accept="image/*" onChange={handleThumbnailFile} />
              </div>
              <div className="form-group">
                <label htmlFor="thumbnailWidth">Thumbnail Width (px, for resizing)</label>
                <input id="thumbnailWidth" name="thumbnailWidth" type="number" min="40" max="1200" value={form.thumbnailWidth} onChange={handleChange} placeholder="e.g. 320" />
              </div>
              {form.thumbnailUrl && (
                <div className="thumb-preview">
                  <img
                    src={form.thumbnailUrl}
                    alt="preview"
                    style={form.thumbnailWidth ? { width: `${form.thumbnailWidth}px`, maxWidth: '100%' } : { maxWidth: '100%' }}
                  />
                </div>
              )}
            </>
          )}
          <div className="form-group">
            <label htmlFor="sectionId">Section</label>
            <select id="sectionId" name="sectionId" value={form.sectionId} onChange={handleChange}>
              <option value="">No section</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <VisibilityEditor
              visibleTiers={form.visibleTiers || []}
              visibleUserIds={form.visibleUserIds || []}
              users={users}
              hiddenWhenEmpty={false}
              onChange={({ visibleTiers, visibleUserIds }) => setForm({ ...form, visibleTiers, visibleUserIds })}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      )}

      <div className="sections-manager">
        <h2>Dashboard Sections</h2>
        <p className="admin-subtitle">Drag sections to reorder them, and drag items within a section to reorder content.</p>
        {sections.length === 0 ? (
          <p className="empty-state">No sections yet.</p>
        ) : (
          <div className="sections-list">
            {sections.map((s) => (
              <div
                key={s.id}
                className={`section-item ${draggedSectionId === s.id ? 'dragging' : ''} ${dragOverSectionId === s.id ? 'drag-over' : ''}`}
                draggable
                onDragStart={(e) => handleSectionDragStart(e, s.id)}
                onDragOver={(e) => handleSectionDragOver(e, s.id)}
                onDrop={(e) => handleSectionDrop(e, s.id)}
                onDragEnd={handleSectionDragEnd}
              >
                <div className="section-header">
                  <span className="drag-handle" title="Drag to reorder">⠿</span>
                  <h3>{s.name}</h3>
                  <div className="section-controls">
                    <button className="btn btn-sm" onClick={() => handleEditSection(s)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteSection(s)}>Delete</button>
                  </div>
                </div>
                <div className="section-paddles">
                  {itemsForSection(s.id).length === 0 ? (
                    <p className="empty-state">No content in this section.</p>
                  ) : (
                    itemsForSection(s.id).map((it) => renderItemRow(it, s.id))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sections-manager">
        <h2>Unassigned Content</h2>
        <div className="sections-list">
          <div className="section-item">
            <div className="section-paddles">
              {unassignedItems.length === 0 ? (
                <p className="empty-state">No unassigned content.</p>
              ) : (
                unassignedItems.map((it) => renderItemRow(it, ''))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
